
import { FormAnswers } from './qualification';

export interface EducationContent {
  snapshot: string[];
  insights: string[];
  whatThisMeans: string;
  recommendedOptions?: string[];
  nextSteps?: string;
  scenarioAnalysis?: {
    title: string;
    summary: string;
    fundingOutlook: 'excellent' | 'good' | 'moderate' | 'limited';
    keyFactors: { label: string; status: 'positive' | 'neutral' | 'caution'; detail: string }[];
    recommendedProducts: { name: string; fit: 'best' | 'good' | 'possible'; reason: string }[];
    estimatedRange?: { min: string; max: string };
    approvalLikelihood?: string;
    tips: string[];
  };
}

// Labels for display
const timeLabels: Record<string, string> = {
  '<4mo': 'Less than 4 months',
  '4-12mo': '4-12 months',
  '12-24mo': '12-24 months',
  '24+mo': '24+ months'
};

const revenueLabels: Record<string, string> = {
  '<10k': 'Under $10,000',
  '10-29k': '$10,000 - $29,999',
  '30-49k': '$30,000 - $49,999',
  '50-99k': '$50,000 - $99,999',
  '100k+': '$100,000+'
};

const industryLabels: Record<string, string> = {
  'retail': 'Retail',
  'restaurant': 'Restaurant / Food Service',
  'construction': 'Construction',
  'healthcare': 'Healthcare',
  'professional-services': 'Professional Services',
  'manufacturing': 'Manufacturing',
  'transportation': 'Transportation / Logistics',
  'real-estate': 'Real Estate',
  'technology': 'Technology',
  'hospitality': 'Hospitality',
  'automotive': 'Automotive',
  'other': 'Other'
};

const purposeLabels: Record<string, string> = {
  'working-capital': 'Working Capital',
  'equipment': 'Equipment Purchase',
  'expansion': 'Business Expansion',
  'inventory': 'Inventory Purchase',
  'real-estate': 'Real Estate / Property',
  'debt-consolidation': 'Debt Consolidation',
  'marketing': 'Marketing / Advertising',
  'hiring': 'Hiring / Payroll',
  'other': 'Other'
};

const goalLabels: Record<string, string> = {
  'mca': 'Merchant Cash Advance',
  'line-of-credit': 'Line of Credit',
  'term-loan': 'Term Loan',
  'equipment': 'Equipment Financing',
  'sba-cre': 'SBA / Commercial Real Estate'
};

const creditLabels: Record<string, string> = {
  '<600': 'Under 600',
  '600-649': '600-649',
  '650-699': '650-699',
  '700+': '700+'
};

// Calculate funding outlook based on all factors
function calculateFundingOutlook(answers: FormAnswers): 'excellent' | 'good' | 'moderate' | 'limited' {
  let score = 0;
  
  // Time in business scoring
  if (answers.timeInBusiness === '24+mo') score += 3;
  else if (answers.timeInBusiness === '12-24mo') score += 2;
  else if (answers.timeInBusiness === '4-12mo') score += 1;
  else if (answers.timeInBusiness === '<4mo') score += 0;
  
  // Revenue scoring
  if (answers.monthlyRevenue === '100k+') score += 3;
  else if (answers.monthlyRevenue === '50-99k') score += 2.5;
  else if (answers.monthlyRevenue === '30-49k') score += 2;
  else if (answers.monthlyRevenue === '10-29k') score += 1;
  else if (answers.monthlyRevenue === '<10k') score += 0;
  
  // Credit scoring
  if (answers.creditScore === '700+') score += 2;
  else if (answers.creditScore === '650-699') score += 1.5;
  else if (answers.creditScore === '600-649') score += 1;
  else if (answers.creditScore === '<600') score += 0.5;
  
  if (score >= 7) return 'excellent';
  if (score >= 5) return 'good';
  if (score >= 3) return 'moderate';
  return 'limited';
}

// Generate key factors analysis
function generateKeyFactors(answers: FormAnswers): { label: string; status: 'positive' | 'neutral' | 'caution'; detail: string }[] {
  const factors: { label: string; status: 'positive' | 'neutral' | 'caution'; detail: string }[] = [];
  
  if (answers.timeInBusiness) {
    if (answers.timeInBusiness === '24+mo') {
      factors.push({ label: 'Business Maturity', status: 'positive', detail: 'Established track record opens premium options' });
    } else if (answers.timeInBusiness === '12-24mo') {
      factors.push({ label: 'Business Maturity', status: 'positive', detail: 'Meets most lender requirements' });
    } else if (answers.timeInBusiness === '4-12mo') {
      factors.push({ label: 'Business Maturity', status: 'neutral', detail: 'Some options available, building history' });
    } else {
      factors.push({ label: 'Business Maturity', status: 'caution', detail: 'Limited history restricts traditional options' });
    }
  }
  
  if (answers.monthlyRevenue) {
    if (answers.monthlyRevenue === '100k+' || answers.monthlyRevenue === '50-99k') {
      factors.push({ label: 'Revenue Strength', status: 'positive', detail: 'Strong cash flow supports larger funding amounts' });
    } else if (answers.monthlyRevenue === '30-49k' || answers.monthlyRevenue === '10-29k') {
      factors.push({ label: 'Revenue Strength', status: 'neutral', detail: 'Adequate for most working capital products' });
    } else {
      factors.push({ label: 'Revenue Strength', status: 'caution', detail: 'May limit funding amounts and options' });
    }
  }
  
  if (answers.creditScore) {
    if (answers.creditScore === '700+') {
      factors.push({ label: 'Credit Profile', status: 'positive', detail: 'Qualifies for best rates and terms' });
    } else if (answers.creditScore === '650-699') {
      factors.push({ label: 'Credit Profile', status: 'positive', detail: 'Good standing for most products' });
    } else if (answers.creditScore === '600-649') {
      factors.push({ label: 'Credit Profile', status: 'neutral', detail: 'Alternative lenders available' });
    } else {
      factors.push({ label: 'Credit Profile', status: 'caution', detail: 'Revenue-based options recommended' });
    }
  }
  
  // Industry-specific factor
  if (answers.industry) {
    const highRiskIndustries = ['restaurant', 'construction', 'hospitality'];
    const lowRiskIndustries = ['healthcare', 'professional-services', 'technology'];
    
    if (lowRiskIndustries.includes(answers.industry)) {
      factors.push({ label: 'Industry Risk', status: 'positive', detail: `${industryLabels[answers.industry]} viewed favorably by lenders` });
    } else if (highRiskIndustries.includes(answers.industry)) {
      factors.push({ label: 'Industry Risk', status: 'neutral', detail: `${industryLabels[answers.industry]} has specialized funding options` });
    }
  }
  
  // Purpose alignment
  if (answers.fundingPurpose && answers.goal) {
    const goodMatches: Record<string, string[]> = {
      'equipment': ['equipment'],
      'real-estate': ['sba-cre'],
      'working-capital': ['mca', 'line-of-credit'],
      'expansion': ['term-loan', 'sba-cre'],
      'inventory': ['line-of-credit', 'mca']
    };
    
    if (goodMatches[answers.fundingPurpose]?.includes(answers.goal)) {
      factors.push({ label: 'Product Match', status: 'positive', detail: `${goalLabels[answers.goal]} aligns well with ${purposeLabels[answers.fundingPurpose].toLowerCase()}` });
    }
  }
  
  return factors;
}

// Generate recommended products based on scenario
function generateRecommendedProducts(answers: FormAnswers): { name: string; fit: 'best' | 'good' | 'possible'; reason: string }[] {
  const products: { name: string; fit: 'best' | 'good' | 'possible'; reason: string }[] = [];
  
  const hasStrongRevenue = ['50-99k', '100k+'].includes(answers.monthlyRevenue || '');
  const hasGoodCredit = ['650-699', '700+'].includes(answers.creditScore || '');
  const isEstablished = ['12-24mo', '24+mo'].includes(answers.timeInBusiness || '');
  const isNew = answers.timeInBusiness === '<4mo';
  
  // MCA recommendation
  if (answers.monthlyRevenue && answers.monthlyRevenue !== '<10k') {
    const mcaFit = hasStrongRevenue ? 'best' : 'good';
    products.push({
      name: 'Merchant Cash Advance',
      fit: mcaFit,
      reason: hasStrongRevenue 
        ? 'Your revenue supports quick approval with flexible repayment'
        : 'Revenue-based approval, less credit dependent'
    });
  }
  
  // Line of Credit recommendation
  if (hasGoodCredit && isEstablished) {
    products.push({
      name: 'Line of Credit',
      fit: 'best',
      reason: 'Your credit and history qualify for revolving access'
    });
  } else if (hasGoodCredit || isEstablished) {
    products.push({
      name: 'Line of Credit',
      fit: 'good',
      reason: 'May qualify with additional documentation'
    });
  }
  
  // Term Loan recommendation
  if (isEstablished && hasStrongRevenue) {
    products.push({
      name: 'Term Loan',
      fit: hasGoodCredit ? 'best' : 'good',
      reason: 'Business maturity and revenue support fixed-term financing'
    });
  }
  
  // Equipment Financing
  if (answers.fundingPurpose === 'equipment' || answers.goal === 'equipment') {
    products.push({
      name: 'Equipment Financing',
      fit: 'best',
      reason: 'Equipment as collateral improves approval odds and rates'
    });
  }
  
  // SBA Loans
  if (isEstablished && hasGoodCredit && hasStrongRevenue) {
    products.push({
      name: 'SBA Loan',
      fit: 'good',
      reason: 'Your profile may qualify for government-backed programs'
    });
  }
  
  return products.slice(0, 4);
}

// Generate funding range estimate
function generateFundingRange(answers: FormAnswers): { min: string; max: string } | undefined {
  if (!answers.monthlyRevenue) return undefined;
  
  const ranges: Record<string, { min: number; max: number }> = {
    '<10k': { min: 5000, max: 25000 },
    '10-29k': { min: 15000, max: 75000 },
    '30-49k': { min: 30000, max: 150000 },
    '50-99k': { min: 75000, max: 350000 },
    '100k+': { min: 150000, max: 1000000 }
  };
  
  const range = ranges[answers.monthlyRevenue];
  if (!range) return undefined;
  
  // Adjust based on time in business
  let multiplier = 1;
  if (answers.timeInBusiness === '24+mo') multiplier = 1.2;
  else if (answers.timeInBusiness === '<4mo') multiplier = 0.5;
  
  return {
    min: `$${Math.round(range.min * multiplier).toLocaleString()}`,
    max: `$${Math.round(range.max * multiplier).toLocaleString()}`
  };
}

// Generate scenario-specific tips
function generateScenarioTips(answers: FormAnswers): string[] {
  const tips: string[] = [];
  
  // Time-based tips
  if (answers.timeInBusiness === '<4mo') {
    tips.push('Focus on building 3+ months of consistent bank deposits');
  } else if (answers.timeInBusiness === '4-12mo') {
    tips.push('Reaching 12 months will significantly expand your options');
  }
  
  // Revenue-based tips
  if (answers.monthlyRevenue === '<10k') {
    tips.push('Growing revenue above $10K/month opens more doors');
  } else if (answers.monthlyRevenue && ['50-99k', '100k+'].includes(answers.monthlyRevenue)) {
    tips.push('Your revenue qualifies you to negotiate better terms');
  }
  
  // Credit-based tips
  if (answers.creditScore === '<600') {
    tips.push('Revenue-based products like MCAs prioritize cash flow over credit');
  } else if (answers.creditScore === '700+') {
    tips.push('Your credit score qualifies you for premium rate programs');
  }
  
  // Industry-specific tips
  if (answers.industry === 'restaurant') {
    tips.push('Daily credit card volume is key for MCA approval');
  } else if (answers.industry === 'construction') {
    tips.push('Invoice factoring can help bridge payment gaps');
  } else if (answers.industry === 'healthcare') {
    tips.push('Medical equipment financing often has favorable terms');
  }
  
  // Purpose-specific tips
  if (answers.fundingPurpose === 'equipment') {
    tips.push('Equipment as collateral typically means better rates');
  } else if (answers.fundingPurpose === 'expansion') {
    tips.push('Prepare a growth plan to strengthen your application');
  }
  
  // Goal-specific tips
  if (answers.goal === 'sba-cre') {
    tips.push('SBA loans require extensive documentation—start gathering now');
  } else if (answers.goal === 'line-of-credit') {
    tips.push('Maintain positive bank balances to strengthen approval odds');
  }
  
  return tips.slice(0, 4);
}

// Generate scenario title
function generateScenarioTitle(answers: FormAnswers): string {
  const parts: string[] = [];
  
  if (answers.industry) {
    parts.push(industryLabels[answers.industry]);
  }
  
  if (answers.timeInBusiness) {
    if (answers.timeInBusiness === '<4mo') parts.push('Startup');
    else if (answers.timeInBusiness === '24+mo') parts.push('Established');
  }
  
  if (answers.fundingPurpose) {
    parts.push(purposeLabels[answers.fundingPurpose]);
  }
  
  if (parts.length === 0) return 'Your Funding Scenario';
  
  return parts.join(' • ');
}

// Generate scenario summary
function generateScenarioSummary(answers: FormAnswers, outlook: string): string {
  const industry = answers.industry ? industryLabels[answers.industry].toLowerCase() : 'your';
  const time = answers.timeInBusiness ? timeLabels[answers.timeInBusiness].toLowerCase() : '';
  const revenue = answers.monthlyRevenue ? revenueLabels[answers.monthlyRevenue] : '';
  const purpose = answers.fundingPurpose ? purposeLabels[answers.fundingPurpose].toLowerCase() : 'business needs';
  const product = answers.goal ? goalLabels[answers.goal] : '';
  
  let summary = '';
  
  if (outlook === 'excellent') {
    summary = `Your ${industry} business with ${time} of operation and ${revenue} monthly revenue is well-positioned for funding. `;
  } else if (outlook === 'good') {
    summary = `Your ${industry} business shows solid fundamentals for ${purpose} financing. `;
  } else if (outlook === 'moderate') {
    summary = `Your business profile qualifies for several funding options, though some products may have specific requirements. `;
  } else {
    summary = `While traditional options may be limited, alternative funding products are available for your situation. `;
  }
  
  if (product) {
    summary += `A ${product} could be a strong fit for your ${purpose} goals.`;
  }
  
  return summary;
}

// Generate approval likelihood
function generateApprovalLikelihood(answers: FormAnswers, outlook: string): string {
  if (outlook === 'excellent') return 'Very High';
  if (outlook === 'good') return 'High';
  if (outlook === 'moderate') return 'Moderate';
  return 'Selective';
}

export function getEducationContent(answers: FormAnswers, isQualified: boolean, tier?: string): EducationContent {
  const snapshot: string[] = [];
  const insights: string[] = [];
  let whatThisMeans = '';
  let recommendedOptions: string[] | undefined;
  let nextSteps: string | undefined;
  let scenarioAnalysis: EducationContent['scenarioAnalysis'] | undefined;

  // Build snapshot
  if (answers.timeInBusiness) {
    snapshot.push(`Time in Business: ${timeLabels[answers.timeInBusiness] || answers.timeInBusiness}`);
  }
  if (answers.monthlyRevenue) {
    snapshot.push(`Monthly Revenue: ${revenueLabels[answers.monthlyRevenue] || answers.monthlyRevenue}`);
  }
  if (answers.industry) {
    snapshot.push(`Industry: ${industryLabels[answers.industry] || answers.industry}`);
  }
  if (answers.fundingPurpose) {
    snapshot.push(`Purpose: ${purposeLabels[answers.fundingPurpose] || answers.fundingPurpose}`);
  }
  if (answers.fundingAmount) {
    const amount = parseInt(answers.fundingAmount);
    if (!isNaN(amount)) {
      snapshot.push(`Funding Amount: $${amount.toLocaleString()}`);
    }
  }
  if (answers.goal) {
    snapshot.push(`Product Interest: ${goalLabels[answers.goal] || answers.goal}`);
  }
  if (answers.creditScore) {
    snapshot.push(`Credit Range: ${creditLabels[answers.creditScore] || answers.creditScore}`);
  }

  // Generate scenario analysis if we have at least 2 answers
  const answeredCount = Object.values(answers).filter(Boolean).length;
  
  if (answeredCount >= 2) {
    const outlook = calculateFundingOutlook(answers);
    const keyFactors = generateKeyFactors(answers);
    const recommendedProducts = generateRecommendedProducts(answers);
    const estimatedRange = generateFundingRange(answers);
    const tips = generateScenarioTips(answers);
    
    scenarioAnalysis = {
      title: generateScenarioTitle(answers),
      summary: generateScenarioSummary(answers, outlook),
      fundingOutlook: outlook,
      keyFactors,
      recommendedProducts,
      estimatedRange,
      approvalLikelihood: generateApprovalLikelihood(answers, outlook),
      tips
    };
  }

  // Build "What This Means" for early stages
  if (!answers.timeInBusiness) {
    whatThisMeans = 'Your business operating history is the foundation of funding eligibility. Most traditional lenders require at least 12 months of documented business operations.';
  } else if (!answers.monthlyRevenue) {
    whatThisMeans = 'Monthly revenue demonstrates your ability to generate consistent cash flow and service debt obligations.';
  }

  // Next steps for qualified applicants
  if (isQualified && answers.goal) {
    nextSteps = 'Based on your profile, you meet the initial qualification criteria. The full application will match you with specific lender offers.';
  }

  return {
    snapshot,
    insights,
    whatThisMeans,
    recommendedOptions,
    nextSteps,
    scenarioAnalysis
  };
}
